import { DataTable, type DataTableProps } from "@appretio/ui-kit";
import type {
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  ColumnEventType,
  ColumnVisibleEvent,
  FirstDataRenderedEvent,
  GridOptions
} from "ag-grid-enterprise";
import React, { useCallback, useMemo } from "react";
import { container, tableLabel, tableSection } from "./MultiTableGrid.css";
import { DEFAULT_ROW_LABEL_FIELD, pivotToRowData } from "./multiTableGridUtils";

/**
 * Sources a column-visibility event has when the user, not the code, moved
 * a column: the column menu, the columns tool panel and their drag targets.
 */
const USER_VISIBILITY_SOURCES = new Set<ColumnEventType>([
  "columnMenu",
  "contextMenu",
  "toolPanelUi",
  "toolPanelDragAndDrop",
  "uiColumnDragged"
]);

const STATIC_GRID_OPTIONS: GridOptions = {
  defaultColDef: {
    flex: undefined,
    width: 120,
    minWidth: 80
  },
  // Commit an open editor on blur, so Apply and Save see the typed value.
  stopEditingWhenCellsLoseFocus: true
};

/** Describes a single cell edit in one of the tables. */
export interface MultiTableCellValueChanged {
  /** Index of the table where the edit occurred. */
  tableIndex: number;
  /** Index of the row within the table. */
  rowIndex: number;
  /** Column identifier (e.g. "GS1|PTD"). */
  column: string;
  /** The new cell value after the edit. */
  newValue: number;
  /** The previous cell value before the edit. */
  oldValue: number;
}

/** Props for the MultiTableGrid component. */
export interface MultiTableGridProps {
  /** Labels for each table (e.g. ["Casual", "Formal", "TOTAL"]). */
  tableLabels: string[];

  /** Labels for rows in each table — different tables may have different rows. */
  rowLabelsPerTable: string[][];

  /**
   * Columnar data: column name to 2D number array [tables][rows].
   * Column names carry metric|agg info (e.g. "GS1|PTD", "GS1|PTG").
   */
  data: Record<string, (number | null)[][]>;

  /** AG Grid column definitions controlling which data columns are visible. */
  columnDefs: (ColDef | ColGroupDef)[];

  /** Field name for the pinned-left row label column. Defaults to "row_label". */
  rowLabelField?: string;

  /** Called when a cell value changes in any table. */
  onCellValueChanged?: (event: MultiTableCellValueChanged) => void;

  /**
   * Called when the user shows or hides a metric's column in any table —
   * the column menu or the columns panel, never a defs rebuild. The owner
   * folds it back into `columnDefs`, so every table follows.
   */
  onMetricVisible?: (metric: string, visible: boolean) => void;

  /** Additional AG Grid props passed through to each grid instance. */
  gridProps?: Partial<DataTableProps>;
}

/**
 * Renders multiple AG Grid tables from columnar 2D data.
 *
 * Each entry in `tableLabels` produces one grid. Different tables may
 * have different rows (per `rowLabelsPerTable`).
 */
export const MultiTableGrid = React.memo(function MultiTableGrid({
  tableLabels,
  rowLabelsPerTable,
  data,
  columnDefs,
  rowLabelField = DEFAULT_ROW_LABEL_FIELD,
  onCellValueChanged,
  onMetricVisible,
  gridProps
}: MultiTableGridProps) {
  const rowDataPerTable = useMemo(
    () =>
      tableLabels.map((_, tableIndex) =>
        pivotToRowData(
          data,
          tableIndex,
          rowLabelsPerTable[tableIndex] ?? [],
          rowLabelField
        )
      ),
    [data, tableLabels, rowLabelsPerTable, rowLabelField]
  );

  const handleFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent) => {
      event.api.autoSizeAllColumns(false);
    },
    []
  );

  const handleCellValueChanged = useCallback(
    (tableIndex: number, event: CellValueChangedEvent) => {
      if (!onCellValueChanged) return;
      const tableRowLabels = rowLabelsPerTable[tableIndex] ?? [];
      const rowIndex = tableRowLabels.indexOf(event.data[rowLabelField]);
      if (rowIndex === -1) return;

      // Read from row data, not event.newValue: a valueGetter column (index
      // mode) re-derives newValue to the displayed value, whereas data[field]
      // holds the stored value the setter wrote.
      const field = event.colDef.field ?? "";
      onCellValueChanged({
        tableIndex,
        rowIndex,
        column: field,
        newValue: Number(event.data[field]),
        oldValue: event.oldValue
      });
    },
    [onCellValueChanged, rowLabelsPerTable, rowLabelField]
  );

  const handleColumnVisible = useCallback(
    (event: ColumnVisibleEvent) => {
      if (
        !onMetricVisible ||
        event.visible === undefined ||
        !USER_VISIBILITY_SOURCES.has(event.source)
      ) {
        return;
      }
      // Column ids are ``metric|agg``; the choice is about the metric. The
      // row label column is not a metric.
      const metrics = new Set(
        (event.columns ?? [])
          .map((column) => column.getColId())
          .filter((colId) => colId !== rowLabelField)
          .map((colId) => colId.split("|")[0])
      );
      for (const metric of metrics) onMetricVisible(metric, event.visible);
    },
    [onMetricVisible, rowLabelField]
  );

  return (
    <div className={container}>
      {tableLabels.map((label, tableIndex) => (
        <div key={label} className={tableSection}>
          <div className={tableLabel}>{label}</div>
          <DataTable
            columnDefs={columnDefs}
            rowData={rowDataPerTable[tableIndex]}
            domLayout="autoHeight"
            gridOptions={STATIC_GRID_OPTIONS}
            getRowId={(params) => String(params.data[rowLabelField])}
            onFirstDataRendered={handleFirstDataRendered}
            onCellValueChanged={(event) =>
              handleCellValueChanged(tableIndex, event)
            }
            onColumnVisible={handleColumnVisible}
            {...gridProps}
          />
        </div>
      ))}
    </div>
  );
});
